import { useState } from "react";
import {
  Card, Table, Button, Tag, Select, Input, Row, Col, Statistic,
} from "antd";
import { RefreshCcw, AlertTriangle, ShieldOff } from "lucide-react";
import { useRateLimitLogList, useRateLimitLogStats } from "../../../api-hooks/rateLimitLogs";
import { TRateLimitLog } from "../../../types";
import type { ColumnsType } from "antd/es/table";

// Rate limiting logs — tracks API request frequency.
// 'blocked=true' means the client exceeded the rate limit and was denied.

const { Option } = Select;

export default function RateLimitLogs() {
  const [blockedFilter, setBlockedFilter] = useState<string>("all");
  const [identifierSearch, setIdentifierSearch] = useState<string>("");
  const [endpointSearch, setEndpointSearch] = useState<string>("");

  const queryParams = {
    limit: 50,
    ...(blockedFilter !== "all" ? { blocked: blockedFilter === "true" } : {}),
    ...(identifierSearch.trim() ? { identifier: identifierSearch.trim() } : {}),
    ...(endpointSearch.trim() ? { endpoint: endpointSearch.trim() } : {}),
  };

  const { data, isLoading, isFetching, refetch } = useRateLimitLogList(queryParams);
  const { data: stats } = useRateLimitLogStats();
  const logs: TRateLimitLog[] = data?.data ?? [];

  const columns: ColumnsType<TRateLimitLog> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      render: (val: number) => <span className="text-xs text-slate-400">#{val}</span>,
    },
    {
      title: "Identifier",
      dataIndex: "identifier",
      key: "identifier",
      render: (val: string) => (
        // identifier = IP address or API key fingerprint — truncated for display
        <span className="font-mono text-xs text-slate-700 max-w-[140px] block truncate" title={val}>{val}</span>
      ),
    },
    {
      title: "Endpoint",
      dataIndex: "endpoint",
      key: "endpoint",
      render: (val: string) => (
        <span className="font-mono text-xs text-slate-600">{val}</span>
      ),
    },
    {
      title: "Hit Count",
      dataIndex: "hit_count",
      key: "hit_count",
      render: (val: number) => (
        <span className="text-xs font-semibold text-slate-700">{val}</span>
      ),
    },
    {
      title: "Blocked",
      dataIndex: "blocked",
      key: "blocked",
      render: (val: boolean) =>
        val ? (
          <Tag color="red" icon={<ShieldOff size={10} />}>Yes</Tag>
        ) : (
          <Tag color="default">No</Tag>
        ),
    },
    {
      title: "Window Start",
      dataIndex: "window_start",
      key: "window_start",
      render: (val: string) => (
        <span className="text-xs text-slate-400">{new Date(val).toLocaleString("en-GB")}</span>
      ),
    },
    {
      title: "Window End",
      dataIndex: "window_end",
      key: "window_end",
      render: (val: string) => (
        <span className="text-xs text-slate-400">{new Date(val).toLocaleString("en-GB")}</span>
      ),
    },
    {
      title: "Logged At",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => (
        <span className="text-xs text-slate-400">{new Date(val).toLocaleString("en-GB")}</span>
      ),
    },
  ];

  const topEndpoint = stats?.top_endpoints?.[0]?.endpoint ?? "—";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Rate Limit Logs</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Tracks API request frequency — showing last 50 records. Blocked=true means the client was denied.
          </p>
        </div>
        <Button
          icon={<RefreshCcw size={14} />}
          onClick={() => refetch()}
          loading={isFetching}
        >
          Refresh
        </Button>
      </div>

      {/* Stats cards */}
      <Row gutter={16} className="mb-6">
        <Col xs={24} sm={12}>
          <Card className="rounded-2xl border border-slate-200 shadow-sm">
            <Statistic
              title={<span className="text-slate-500 text-sm">Total Blocked Requests</span>}
              value={stats?.total_blocked ?? 0}
              valueStyle={{ color: "#dc2626", fontWeight: 700 }}
              prefix={<AlertTriangle size={16} className="text-red-500 mr-1" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card className="rounded-2xl border border-slate-200 shadow-sm">
            <div className="text-slate-500 text-sm mb-1">Top Blocked Endpoint</div>
            <div
              className="font-mono text-sm font-semibold text-slate-700 truncate"
              title={topEndpoint}
            >
              {topEndpoint}
            </div>
            {stats?.top_endpoints?.[0]?.hit_count && (
              <div className="text-xs text-slate-400 mt-0.5">{stats.top_endpoints[0].hit_count} hits</div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Blocked:</span>
          <Select value={blockedFilter} onChange={setBlockedFilter} style={{ width: 130 }}>
            <Option value="all">All</Option>
            <Option value="true">Yes (Blocked)</Option>
            <Option value="false">No</Option>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Identifier:</span>
          <Input
            placeholder="IP / API key..."
            value={identifierSearch}
            onChange={(e) => setIdentifierSearch(e.target.value)}
            style={{ width: 180 }}
            allowClear
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Endpoint:</span>
          <Input
            placeholder="/api/v1/..."
            value={endpointSearch}
            onChange={(e) => setEndpointSearch(e.target.value)}
            style={{ width: 180 }}
            allowClear
          />
        </div>
        {data?.extra && (
          <span className="text-xs text-slate-400 ml-2">{data.extra.total} total</span>
        )}
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        {/* No client-side pagination — server returns max 50 records, use filters to narrow down */}
        <Table
          dataSource={logs}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading || isFetching}
          size="middle"
          scroll={{ x: 1000 }}
          locale={{ emptyText: "No rate limit logs found for the selected filters." }}
          rowClassName={(record) =>
            record.blocked ? "bg-red-50/40" : ""
          }
        />
      </Card>
    </div>
  );
}
